import { Icon, Typography, Link, Box, Alert, AlertTypography } from '@ace-clublabs/shared-component-library';
import AutoIcon from '../assets/auto-icon.png';
import HomeIcon from '../assets/home-icon.png';
import CondoIcon from '../assets/condo-icon.png';
import MembershipIcon from '../assets/membership-icon.png';
import EarthquakeIcon from '../assets/earthquake-icon.png';
import WatercraftIcon from '../assets/watercraft-icon.png';
import UmbrellaIcon from '../assets/umbrella-icon.png';
import RentalIcon from '../assets/rental-icon.png';
import CheckIcon from '../assets/radio-check1.png';
import { Status, PolicyTitle, IInsuranceProduct, IMembershipProduct, PolicyType, ErrorType } from '../shared/types';
import { PaymentType, CardBrand } from '@ace-clublabs/payment-types';
import { Pages } from '../shared/pages';
import { useNavigate } from 'react-router-dom';
import { useGlobalState } from '../shared/state';
import { sources } from '../api/sources';
import { ReactNode, useEffect, useState } from 'react';

export const AutoPayProduct: React.FC<AutoPayProductProps> = (props) => {
  return (
    <AutoPayProductView
      product={props.product}
      disableAutoPayEnrollAndUpdateCTA={props.disableAutoPayEnrollAndUpdateCTA}
      updateProcessing={props.updateProcessing}
      showLoading={props.showLoading}
      handleChange={props.handleChange}
      referrer={props.referrer}
      phoneNumber={props.phoneNumber}
    />
  );
};

export interface AutoPayProductProps {
  product: IInsuranceProduct | IMembershipProduct;
  disableAutoPayEnrollAndUpdateCTA: boolean;
  handleChange: (product: IInsuranceProduct | IMembershipProduct) => void;
  showLoading?: any;
  updateProcessing?: any;
  referrer?: string;
  phoneNumber?: string;
}

export const AutoPayProductView: React.FC<AutoPayProductProps> = (props) => {
  const {
    product,
    disableAutoPayEnrollAndUpdateCTA,
    handleChange,
    updateProcessing,
    showLoading,
    referrer,
    phoneNumber,
  } = props;
  const [selectedProduct, setSelectedProduct] = useGlobalState('selectedProduct');
  const [isInitialRender, setIsInitialRender] = useGlobalState('isInitialRender');
  const [paymentUpdated, setPaymentUpdated] = useGlobalState('paymentUpdated');
  const [cancelMembershipAutoPayFailed] = useGlobalState('cancelMembershipAutoPayFailed');
  const [brokerRegion] = useGlobalState('brokerRegion');
  const [discount, setDiscount] = useGlobalState('discount');
  const [, setNewEnroll] = useGlobalState('newEnroll');
  const {
    policyType,
    productId,
    autoPay,
    benefitLevel = '',
    inRenewal,
    inArrears,
    hasPIT,
    minDue,
    billPlan,
  } = product as any;
  const { enrolled, paymentDetails, offline, eligible } = autoPay!;
  const [productEnrolled] = useState(enrolled);

  const last4 = paymentDetails?.last4 || paymentDetails?.accountLast4 || '';
  const paymentType = paymentDetails?.paymentType ? paymentDetails.paymentType : '';
  const brand = paymentDetails?.brand ? paymentDetails.brand : '';
  const isMembership = policyType === PolicyType.Membership;
  const isMssEnrollShortcut = isMembership && !enrolled && referrer?.toLowerCase().includes('membership/selfservice');
  const autoPayUpdated = paymentUpdated && productId === selectedProduct.productId;
  const isMDO = isMembership && billPlan?.toLowerCase() === 'mp';
  const isADBA = isMembership && ['ad', 'ba'].includes(billPlan?.toLowerCase());

  const navigate = useNavigate();

  let status = '';
  let statusIcon = '';

  const handleCancel = (e: any) => {
    setIsInitialRender(false);
    e.preventDefault();
    setSelectedProduct(props.product);
    if (hasPIT) {
      return navigate(Pages.Error, { state: { type: ErrorType.MEMBERSHIP_PIT_ERROR } });
    } else navigate(Pages.CancelMembership);
  };

  const handleEnroll = async () => {
    setIsInitialRender(false);
    setSelectedProduct(product);
    setPaymentUpdated(false);
    setDiscount(0);
    if (offline) {
      return navigate(Pages.GwAutopayIneligibleError);
    }

    const hasMinDue = minDue! > 0;
    const handleDiscount = async () => {
      if (isMembership && (inRenewal || inArrears)) {
        try {
          showLoading(true);
          const { discount } = await sources.getDiscount({
            membershipNumber: productId,
            brokerRegion,
            benefitLevel,
            inArrears,
          });
          if (discount < minDue) {
            setDiscount(discount);
          }
        } catch (e) {
          throw e;
        } finally {
          showLoading(false);
        }
      }
    };

    if (hasMinDue && !hasPIT) {
      try {
        await handleDiscount();
        return navigate(Pages.AutoPayMinDue);
      } catch (e) {
        return navigate(Pages.Error);
      }
    } else if (isMembership && hasMinDue && hasPIT) {
      return navigate(Pages.Error, { state: { type: ErrorType.MEMBERSHIP_PIT_ERROR } });
    } else {
      try {
        await handleDiscount();
      } catch (e) {
        return navigate(Pages.Error);
      }
      return navigate(Pages.AutoPayConsent);
    }
  };

  let productIcon: string;
  let productTitle: string;
  let policyTitle: ReactNode;

  switch (policyType) {
    case 'PersonalAuto':
    case 'Auto':
      productTitle = PolicyTitle.Auto;
      productIcon = AutoIcon;
      break;
    case 'Classic':
      productTitle = PolicyTitle.Classic;
      productIcon = AutoIcon;
      break;
    case 'Homeowners':
    case 'Home':
      productTitle = PolicyTitle.Home;
      productIcon = HomeIcon;
      break;
    case 'Condo':
      productTitle = PolicyTitle.Condo;
      productIcon = CondoIcon;
      break;
    case 'Umbrella':
      productTitle = PolicyTitle.Umbrella;
      productIcon = UmbrellaIcon;
      break;
    case 'Watercraft':
      productTitle = PolicyTitle.Watercraft;
      productIcon = WatercraftIcon;
      break;
    case 'Earthquake':
      productTitle = PolicyTitle.Earthquake;
      productIcon = EarthquakeIcon;
      break;
    case 'Rental':
      productTitle = PolicyTitle.Rental;
      productIcon = RentalIcon;
      break;
    case 'Tenant':
      productTitle = PolicyTitle.Tenant;
      productIcon = RentalIcon;
      break;
    case 'Membership':
    default:
      productTitle = PolicyTitle.Membership;
      productIcon = MembershipIcon;
  }

  switch (benefitLevel.toLowerCase()) {
    case 'classic':
      policyTitle = <>Classic member</>;
      break;
    case 'classic rv':
    case 'classic-rv':
      policyTitle = <>Classic RV member</>;
      break;
    case 'plus':
      policyTitle = (
        <>
          Plus<sup>&reg;</sup> member
        </>
      );
      break;
    case 'plus rv':
    case 'plus-rv':
      policyTitle = (
        <>
          Plus<sup>&reg;</sup> RV member
        </>
      );
      break;
    case 'premier':
      policyTitle = (
        <>
          Premier<sup>&reg;</sup> member
        </>
      );
      break;
    case 'premier rv':
    case 'premier-rv':
      policyTitle = (
        <>
          Premier<sup>&reg;</sup> RV member
        </>
      );
      break;
    default:
      policyTitle = `${productTitle} policy`;
  }

  const isVisa: boolean = brand === CardBrand.Visa;
  const isMaster: boolean = brand === CardBrand.MasterCard;
  const isAmex: boolean = brand === CardBrand.AmericanExpress;
  const isDiscover: boolean = brand === CardBrand.Discover;
  const isEcheck: boolean = paymentType === PaymentType.Echeck;
  if (autoPayUpdated) {
    status = Status.Updated;
    statusIcon = CheckIcon;
  }

  useEffect(() => {
    if (isMssEnrollShortcut && isInitialRender) {
      handleEnroll();
    }
  }, []);

  return (
    <Box className="product-container">
      <Box className="product-row">
        <img src={productIcon} alt="product-icon"></img>
        <Box className="policy">
          <Typography variant="b1Bold" className="policy-type" component="span">
            {policyTitle}
          </Typography>
          <Typography variant="b1Regular" className="policy-number" component="span">
            {productId}
          </Typography>
        </Box>
      </Box>
      <Box className="product-payment-info">
        <Box className="info-item">
          <Box className="product-row-autopay">
            <Box className="auto-pay-section">
              <Typography variant="b1Regular" className="auto-pay" component="span">
                Auto Pay:
              </Typography>
              <Typography variant="b1Medium" className="enrollment-status" component="span">
                {isADBA ? 'Not eligible' : productEnrolled ? 'Enrolled' : 'Not enrolled'}
              </Typography>
              {!eligible && (
                <Alert
                  severity="warning"
                  icon={<Icon color="warning" iconName="triangle-exclamation-regular" sx={{ fontSize: 18 }} />}
                  displayCloseButton={false}
                  sx={{ marginTop: '8px' }}
                >
                  <AlertTypography variant="regular">
                    Your {isMembership ? 'membership' : 'policy'} is not eligible to enroll in Auto Pay.
                  </AlertTypography>
                </Alert>
              )}
            </Box>
          </Box>
          <Box className="product-row-card">
            {productEnrolled && (
              <>
                <Box className="payment-info-row">
                  <Box>
                    {isVisa ? (
                      <Icon iconName="visa-logo"></Icon>
                    ) : isAmex ? (
                      <Icon iconName="amex-logo"></Icon>
                    ) : isMaster ? (
                      <Icon iconName="mastercard-logo"></Icon>
                    ) : isDiscover ? (
                      <Icon iconName="discover-logo"></Icon>
                    ) : (
                      isEcheck && (
                        <Typography variant="b1Bold" className="echeck" component="span">
                          E-check
                        </Typography>
                      )
                    )}
                    <Typography variant="b1Regular" className="account-last4" component="span">
                      •••• {last4}
                    </Typography>
                  </Box>
                  <Typography variant="b1Regular" className="row-link" component="span">
                    {!disableAutoPayEnrollAndUpdateCTA && eligible && (
                      <Link
                        id={`change-method-link-${policyType.replace(/\s+/g, '').toLowerCase()}-${productId}`}
                        className="change-method-link"
                        component="button"
                        onClick={() => {
                          setSelectedProduct(product);
                          setNewEnroll('');
                          setPaymentUpdated(false);
                          handleChange(product);
                        }}
                      >
                        Change
                      </Link>
                    )}
                  </Typography>
                </Box>
                <Box className="payment-info-row">
                  {status && (
                    <Box className="payment-status">
                      <img src={statusIcon} alt="payment-status-icon" className="payment-status-icon"></img>
                      <Typography variant="b1Regular" className={`payment-status-${status}-icon`} component="span">
                        {status}
                      </Typography>
                    </Box>
                  )}
                  {(status === Status.Expired || status === Status.ExpiringSoon) && (
                    <Typography variant="b1Regular" className="row-link" component="span">
                      <Link target="_self" href={''} sx={{ fontSize: '18px' }}>
                        Update exp
                      </Link>
                    </Typography>
                  )}
                </Box>
              </>
            )}
          </Box>
        </Box>
        <Box className="info-item">
          {!isADBA && (
            <Box className="product-row-autopay autopay-link-box">
              {!productEnrolled ? (
                <Typography variant="b1Regular" className="row-link" component="span">
                  {!disableAutoPayEnrollAndUpdateCTA && eligible && (
                    <Link
                      id={`enroll-link-${policyType.replace(/\s+/g, '').toLowerCase()}-${productId}`}
                      className="change-method-link"
                      component="button"
                      onClick={() => {
                        handleEnroll();
                      }}
                    >
                      Enroll in Auto Pay
                    </Link>
                  )}
                </Typography>
              ) : (
                isMembership &&
                !disableAutoPayEnrollAndUpdateCTA &&
                eligible && (
                  <Typography
                    variant="b1Regular"
                    className={`row-link${autoPayUpdated ? '-cancel' : ''}`}
                    component="span"
                  >
                    <Link className="membership-cancel-autopay" href={''} onClick={(e) => handleCancel(e)}>
                      Cancel Auto Pay
                    </Link>
                  </Typography>
                )
              )}
            </Box>
          )}
          {isMembership && cancelMembershipAutoPayFailed && (
            <Box className="product-row-card cancel-membership-autopay-failed-box">
              <Alert severity="error" displayCloseButton={false} id="cancel-membership-autopay-failed-alert">
                <AlertTypography>
                  Sorry, we could not complete your request to cancel Auto Pay. Please try again or call us at{' '}
                  <Link variant="b2Regular" target="_self" href={`tel:${phoneNumber}`}>
                    {phoneNumber}
                  </Link>
                </AlertTypography>
              </Alert>
            </Box>
          )}
          {!productEnrolled && isMDO && (
            <Box className="product-row-card mdo-cancel-box">
              <Alert severity="error" displayCloseButton={false} className="mdo-cancel-alert">
                You'll need to enroll in Auto Pay to keep your membership. If you'd prefer not to, please speak with an
                agent.
              </Alert>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};
